import React, { useState, useEffect, useMemo } from 'react';
import { FaDownload, FaFilePdf, FaSearch, FaFilter, FaClock, FaCheckCircle, FaUser, FaBuilding, FaMoneyBillWave, FaPrint, FaSpinner, FaExclamationTriangle } from 'react-icons/fa';
import { standarPelayananService } from '../../services/standarPelayananService';

const StandarPelayanan = () => {
    const [data, setData] = useState([]);
    const [kategoriList, setKategoriList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedKategori, setSelectedKategori] = useState('Semua Kategori');
    const [expandedId, setExpandedId] = useState(null);

    const pdfUrl = '/standar_pelayanan.pdf';

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        const result = await standarPelayananService.getPublicStandar();
        if (result.success) {
            setData(result.data?.data || []);
            setKategoriList(result.data?.kategori || []);
        } else {
            setError(result.message);
        }
        setLoading(false);
    };

    const filteredData = useMemo(() => {
        return data.filter((item) => {
            const matchKategori = selectedKategori === 'Semua Kategori' || item.kategori === selectedKategori;
            const matchSearch = item.jenis?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                item.persyaratan?.some(p => p.toLowerCase().includes(searchTerm.toLowerCase())) ||
                item.produk?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchKategori && (searchTerm === '' || matchSearch);
        });
    }, [data, searchTerm, selectedKategori]);

    const toggleExpand = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = pdfUrl;
        link.download = 'standar_pelayanan.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (loading) {
        return (
            <div className="container mx-auto px-4 py-12 text-center">
                <FaSpinner className="animate-spin text-4xl text-[#2E7D32] mx-auto mb-4" />
                <p className="text-gray-500">Memuat data...</p>
            </div>
        );
    }

    if (error || data.length === 0) {
        return (
            <div className="container mx-auto px-4 py-12">
                <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A] mb-8">
                    Standar Pelayanan
                </h1>
                <div className="card-glass text-center py-12">
                    <FaExclamationTriangle className="text-5xl text-yellow-500 mx-auto mb-4" />
                    <h2 className="text-xl font-bold text-gray-700 mb-2">Data Belum Tersedia</h2>
                    <p className="text-gray-500">
                        {error || 'Standar pelayanan belum diinput oleh admin'}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="container mx-auto px-4 py-12">
            <div className="mb-8">
                <div className="flex items-center space-x-3 mb-2">
                    <FaFilePdf className="text-4xl text-[#2E7D32]" />
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold text-[#0F4C3A]">
                            Standar Pelayanan
                        </h1>
                        <p className="text-gray-600 mt-1">
                            Standar pelayanan BPKAD Kota Magelang
                        </p>
                    </div>
                </div>
            </div>

            {/* Tombol Download */}
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 bg-gradient-to-br from-[#0F4C3A] to-[#2E7D32] rounded-lg flex items-center justify-center">
                            <FaFilePdf className="text-white text-xl" />
                        </div>
                        <div>
                            <h3 className="font-bold text-[#0F4C3A]">Dokumen Standar Pelayanan</h3>
                            <p className="text-sm text-gray-500">PDF • {data.length} Jenis Pelayanan</p>
                        </div>
                    </div>
                    <button
                        onClick={handleDownload}
                        className="btn-primary flex items-center justify-center gap-2 px-6 py-2.5"
                    >
                        <FaDownload />
                        Download PDF
                    </button>
                </div>
            </div>

            {/* Filter */}
            <div className="bg-white rounded-xl shadow-lg p-4 md:p-6 mb-6 border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1">
                        <div className="relative">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Cari jenis pelayanan, persyaratan, atau produk..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] outline-none"
                            />
                        </div>
                    </div>
                    <div className="w-full md:w-64">
                        <div className="relative">
                            <FaFilter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <select
                                value={selectedKategori}
                                onChange={(e) => setSelectedKategori(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2E7D32] outline-none appearance-none bg-white"
                            >
                                <option value="Semua Kategori">Semua Kategori</option>
                                {kategoriList.map((kat) => (
                                    <option key={kat} value={kat}>{kat}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className="mt-3 text-sm text-gray-500">
                    Menampilkan {filteredData.length} dari {data.length} jenis pelayanan
                </div>
            </div>

            {/* Daftar */}
            <div className="space-y-4">
                {filteredData.length === 0 ? (
                    <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                        <p className="text-gray-500 text-lg">Tidak ada data yang sesuai.</p>
                    </div>
                ) : (
                    filteredData.map((item) => (
                        <div key={item.id} className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200 hover:shadow-xl transition-shadow">
                            <div className="p-4 md:p-6 cursor-pointer hover:bg-gray-50" onClick={() => toggleExpand(item.id)}>
                                <div className="flex items-start justify-between gap-4">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                            <span className="text-xs font-semibold bg-[#2E7D32] text-white px-2.5 py-0.5 rounded-full">
                                                {item.kategori}
                                            </span>
                                            {item.waktu && (
                                                <span className="text-xs bg-blue-50 text-[#0F4C3A] px-2.5 py-0.5 rounded-full flex items-center gap-1">
                                                    <FaClock className="text-xs" />
                                                    {item.waktu}
                                                </span>
                                            )}
                                            {item.jumlah_pelaksana && (
                                                <span className="text-xs bg-gray-100 text-gray-600 px-2.5 py-0.5 rounded-full">
                                                    {item.jumlah_pelaksana}
                                                </span>
                                            )}
                                        </div>
                                        <h3 className="text-lg font-bold text-[#0F4C3A]">
                                            {item.id}. {item.jenis}
                                        </h3>
                                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                                            {item.biaya && (
                                                <span className="flex items-center gap-1">
                                                    <FaMoneyBillWave className="text-xs text-[#2E7D32]" />
                                                    {item.biaya}
                                                </span>
                                            )}
                                            {item.produk && (
                                                <span className="flex items-center gap-1">
                                                    <FaCheckCircle className="text-xs text-[#2E7D32]" />
                                                    {item.produk}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0 mt-1">
                                        <span className="text-[#2E7D32] font-medium text-sm">
                                            {expandedId === item.id ? '− Sembunyikan' : '+ Detail'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {expandedId === item.id && (
                                <div className="border-t border-gray-200 p-4 md:p-6 bg-gray-50/50">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {item.dasar_hukum && item.dasar_hukum.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-[#0F4C3A] mb-2 flex items-center gap-2">
                                                    <FaFilePdf className="text-[#2E7D32] text-sm" />
                                                    Dasar Hukum
                                                </h4>
                                                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                    {item.dasar_hukum.map((hukum, idx) => (
                                                        <li key={idx} className="pl-2">{hukum}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                        {item.persyaratan && item.persyaratan.length > 0 && (
                                            <div>
                                                <h4 className="font-semibold text-[#0F4C3A] mb-2 flex items-center gap-2">
                                                    <FaUser className="text-[#2E7D32] text-sm" />
                                                    Persyaratan
                                                </h4>
                                                <ul className="list-disc list-inside space-y-1 text-sm text-gray-700">
                                                    {item.persyaratan.map((syarat, idx) => (
                                                        <li key={idx} className="pl-2">{syarat}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                        )}
                                    </div>

                                    {item.prosedur && item.prosedur.length > 0 && (
                                        <div className="mt-4">
                                            <h4 className="font-semibold text-[#0F4C3A] mb-2 flex items-center gap-2">
                                                <FaBuilding className="text-[#2E7D32] text-sm" />
                                                Sistem, Mekanisme dan Prosedur
                                            </h4>
                                            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700">
                                                {item.prosedur.map((step, idx) => (
                                                    <li key={idx} className="pl-2">{step}</li>
                                                ))}
                                            </ol>
                                        </div>
                                    )}

                                    <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-sm text-gray-600">
                                        {item.waktu && (
                                            <span className="flex items-center gap-2">
                                                <FaClock className="text-[#2E7D32]" />
                                                <strong>Waktu:</strong> {item.waktu}
                                            </span>
                                        )}
                                        {item.biaya && (
                                            <span className="flex items-center gap-2">
                                                <FaMoneyBillWave className="text-[#2E7D32]" />
                                                <strong>Biaya:</strong> {item.biaya}
                                            </span>
                                        )}
                                        {item.produk && (
                                            <span className="flex items-center gap-2">
                                                <FaPrint className="text-[#2E7D32]" />
                                                <strong>Produk:</strong> {item.produk}
                                            </span>
                                        )}
                                        {item.jumlah_pelaksana && (
                                            <span className="flex items-center gap-2">
                                                <FaUser className="text-[#2E7D32]" />
                                                <strong>Pelaksana:</strong> {item.jumlah_pelaksana}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            <div className="mt-6 text-center text-xs text-gray-400 border-t border-gray-200 pt-4">
                <p>Sumber: Keputusan Kepala BPKAD Nomor 067/138/440 Tahun 2026</p>
                <p className="mt-1">Standar Pelayanan pada Badan Pengelola Keuangan dan Aset Daerah Kota Magelang</p>
            </div>
        </div>
    );
};

export default StandarPelayanan;